import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { TranslatedCalling, cleanUrl } from './common';
import { InstagramPreview } from '../../InstagramPreview';
import { AlertCircle, Calendar, Send } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import { CREATE_SOCIAL_POST } from '../../../graphql/mutations';
import { INTEGRATIONS_QUERY } from '../../../graphql/queries';
import { DateTimePicker } from '../../DateTimePicker';
import { AccountSelector } from '../../AccountSelector';
import { PostNowDialog } from '../../PostNowDialog';
import { toast } from 'sonner';
import { useAuth } from '../../../context/auth';

function PostResultCard({ result }: { result: any }) {
  const { t } = useTranslation('chat');
  const { token } = useAuth();
  const [showScheduling, setShowScheduling] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<Date | null>(null);
  const [selectedUserIntegration, setSelectedUserIntegration] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPostConfirmDialog, setShowPostConfirmDialog] = useState(false);

  const { data: integrationsData, loading: integrationsLoading } = useQuery(INTEGRATIONS_QUERY, {
    skip: !token,
  });

  const [createSocialPost] = useMutation(CREATE_SOCIAL_POST, {
    onCompleted: () => {
      toast.success(scheduledFor ? t('tool.generate-post.scheduled') : t('tool.generate-post.created'));
      setShowScheduling(false);
      setScheduledFor(null);
      setSelectedUserIntegration(null);
      setShowPostConfirmDialog(false);
      setIsSubmitting(false);
    },
    onError: (error) => {
      toast.error(t('tool.generate-post.error-scheduling', { message: error.message }));
      setIsSubmitting(false);
      setShowPostConfirmDialog(false);
    },
  });

  const handleScheduleClick = () => {
    setShowScheduling(true);
  };

  const handleSubmitSchedule = async (postDate: Date): Promise<void> => {
    if (!postDate || !selectedUserIntegration) {
      toast.error(t('tool.common.select-date-and-account'));
      return;
    }

    setIsSubmitting(true);

    try {
      const captionText = result ? `${result.title}\n\n${result.content}\n\n${result.hashtags.join(' ')}` : '';
      const cleanedUrl = result?.mediaUrl ? cleanUrl(result.mediaUrl) : '';

      await createSocialPost({
        variables: {
          input: {
            userIntegrationId: selectedUserIntegration.id,
            content: captionText,
            mediaUrls: [cleanedUrl],
            scheduledFor: postDate.toISOString(),
            reel: false,
          },
        },
      });
    } catch (error) {
      console.error('Error scheduling post:', error);
      setIsSubmitting(false);
    }
  };

  const activeIntegrations =
    integrationsData?.integrations?.filter((integration: any) =>
      integration.userIntegrations?.some((ui: any) => ui.status === 'active')
    ) || [];

  useEffect(() => {
    if (!selectedUserIntegration && !integrationsLoading && activeIntegrations.length > 0) {
      const allUserIntegrations = activeIntegrations.flatMap((integration: any) =>
        integration.userIntegrations.map((ui: any) => ui)
      );

      if (allUserIntegrations.length > 0) {
        setSelectedUserIntegration(allUserIntegrations[0]);
      }
    }
  }, [activeIntegrations, selectedUserIntegration, integrationsLoading]);

  const cleanedMediaUrl = result?.mediaUrl ? cleanUrl(result.mediaUrl) : '';

  if (!result || !result.mediaUrl || !result.content || !result.title || !result.hashtags) {
    return (
      <div className="my-6 p-2 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-gray-600" />
        <p className="text-gray-600 text-sm">{t('tool.common.content-not-displayed')}</p>
      </div>
    );
  }

  return (
    <div className="my-6 space-y-4">
      <div className="w-[300px]">
        <InstagramPreview
          username={selectedUserIntegration?.metadata?.username || 'artist'}
          profilePicture={selectedUserIntegration?.metadata?.profilePictureUrl}
          images={[cleanedMediaUrl]}
          caption={result.content}
          isReel={false}
        />

        {token && !showScheduling && (
          <div className="mt-3 flex gap-2">
            <Button
              onClick={handleScheduleClick}
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {t('tool.generate-post.schedule-post')}
            </Button>
            <Button
              onClick={() => {
                setShowPostConfirmDialog(true);
              }}
              className="bg-gradient-to-br from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white border-0"
            >
              <Send className="h-4 w-4 mr-2" />
              {t('tool.common.post-now')}
            </Button>
          </div>
        )}
      </div>

      {token && showScheduling && (
        <div className="space-y-4 max-w-[400px]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('tool.common.account')}</label>
            <AccountSelector
              integrations={activeIntegrations}
              loading={integrationsLoading}
              selectedUserIntegration={selectedUserIntegration}
              onSelect={setSelectedUserIntegration}
            />
          </div>

          {activeIntegrations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('tool.common.date-and-time')}</label>
              <div className="w-full">
                <DateTimePicker date={scheduledFor} onDateChange={setScheduledFor} disablePastDates={true} />
              </div>
            </div>
          )}

          {activeIntegrations.length > 0 && (
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowScheduling(false)}
                className="flex-1 text-sm"
                disabled={isSubmitting}
              >
                {t('tool.common.cancel')}
              </Button>
              <Button
                onClick={() => handleSubmitSchedule(scheduledFor || new Date())}
                disabled={!scheduledFor || !selectedUserIntegration || isSubmitting}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0 text-sm"
              >
                {isSubmitting ? t('tool.common.scheduling') : t('tool.common.schedule')}
              </Button>
            </div>
          )}
        </div>
      )}

      <PostNowDialog
        open={showPostConfirmDialog}
        onOpenChange={setShowPostConfirmDialog}
        onConfirm={() => handleSubmitSchedule(new Date())}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

export const generate_post = {
  type: 'acting',
  call: () => <TranslatedCalling i18nKey="tool.generate-post.creating" />,
  'partial-call': () => <TranslatedCalling i18nKey="tool.generate-post.generating" />,
  result: ({ toolCall }: any) => <PostResultCard result={toolCall.result} />,
};
